import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  UiButton,
  UiDialogActions,
  UiDialogContent,
  UiDialogTitle,
  UiIcon,
} from 'ui-components';
import ExportContentView from 'components/export/steps/ExportContentView';
import ExportContentMoreSettings from 'components/export/steps/ExportContentMoreSettings';
import ExportBulletinContentView from 'components/export-bulletin-content/ExportBulletinContentView';
import { changeExportvalue, setBulletinContentQueryStep } from 'store';
import { exportButtonTheme } from './contentExport.constants';
import useContentExport from './useContentExport';
import ExportTypeStep from './ExportTypeStep';
import {
  StyledContentExport,
  StyledContentExportDialog,
} from './ContentExport.styles';

const ContentExport = ({
  platform,
  excelOnly = false,
  randomSort = true,
  renderTrigger,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    isModalOpen,
    showExcel,
    showBulletin,
    canSelectType,
    hasExcelFields,
    activeStepKey,
    excelError,
    excelLoading,
    excelLimitsLoading,
    excelCount,
    excelLimits,
    bulletinSubmit,
    selectedBulletinId,
    bulletinCount,
    bulletinList,
    handleOpen,
    handleClose,
    handleTypeSelect,
    handleExcelSubmit,
    handleBulletinSubmit,
    goToTypeStep,
    hasError,
    setHasError,
  } = useContentExport({ platform, excelOnly });

  const steps = {
    type: {
      title: t('content:downloadContentExport'),
      view: (
        <ExportTypeStep
          onTypeSelect={handleTypeSelect}
          showExcel={showExcel}
          showBulletin={showBulletin}
        />
      ),
      showBack: false,
      error: null,
      actionsClassName: '',
      moreActions: null,
      onSubmit: null,
    },
    excel: {
      title: t('content:downloadExcel'),
      view: (
        <ExportContentView submit={handleExcelSubmit} randomSort={randomSort} />
      ),
      showBack: canSelectType,
      onBack: goToTypeStep,
      error: excelError,
      actionsClassName: 'excel-actions',
      moreActions: hasExcelFields ? (
        <UiButton
          variant='text'
          onClick={() =>
            dispatch(changeExportvalue({ key: 'step', value: 'step_2' }))
          }
          color='primary'
          className='action-advanced-button'
        >
          {t('common:advancedSettings')}
        </UiButton>
      ) : null,
      onSubmit: handleExcelSubmit,
      submitLabel: t('content:downloadOutput'),
      submitLoading: excelLoading,
      submitDisabled:
        excelLoading ||
        excelLimitsLoading ||
        excelCount <= 0 ||
        excelLimits?.data?.remaining < excelCount,
      submitTestId: 'content-export-excel-submit-btn',
    },
    excelAdvanced: {
      title: t('content:excelAdvancedSettings'),
      view: <ExportContentMoreSettings />,
      showBack: true,
      onBack: () =>
        dispatch(changeExportvalue({ key: 'step', value: 'step_1' })),
      error: excelError,
      actionsClassName: 'excel-actions',
      moreActions: null,
      onSubmit: handleExcelSubmit,
      submitLabel: t('content:downloadOutput'),
      submitLoading: excelLoading,
      submitDisabled:
        excelLoading ||
        excelLimitsLoading ||
        excelCount <= 0 ||
        excelLimits?.data?.remaining < excelCount,
      submitTestId: 'content-export-excel-submit-btn',
    },
    bulletin: {
      title: t('content:bulletinExport.downloadBulletin'),
      view: (
        <ExportBulletinContentView
          hasError={hasError}
          setHasError={setHasError}
        />
      ),
      showBack: canSelectType,
      onBack: goToTypeStep,
      error: bulletinSubmit.error,
      actionsClassName: 'bulletin-actions',
      moreActions: null,
      onSubmit: handleBulletinSubmit,
      submitLabel: t('content:downloadOutput'),
      submitLoading: bulletinSubmit.loading,
      submitDisabled:
        bulletinSubmit.loading ||
        !selectedBulletinId ||
        !bulletinCount ||
        hasError ||
        Number(bulletinCount) <= 0,
      submitTestId: 'export-bulletin-content-submit-btn',
    },
    createBulletin: {
      title: t('content:bulletinExport.createBulletin'),
      view: (
        <ExportBulletinContentView
          hasError={hasError}
          setHasError={setHasError}
        />
      ),
      showBack: bulletinList.data?.length > 0,
      onBack: () => dispatch(setBulletinContentQueryStep('selectBulletin')),
      error: bulletinSubmit.error,
      actionsClassName: 'bulletin-actions',
      moreActions: null,
      onSubmit: null,
    },
  };

  const currentStep = steps[activeStepKey];

  if (!showExcel && !showBulletin) return null;

  return (
    <StyledContentExport>
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <ThemeProvider theme={exportButtonTheme}>
          <UiButton
            variant='outlined'
            onClick={handleOpen}
            data-testid='content-export-btn'
          >
            <UiIcon
              icon='ri-file-download-line'
              color='#454545'
              hover='#454545'
            />
            {t('content:downloadOutput')}
          </UiButton>
        </ThemeProvider>
      )}

      <StyledContentExportDialog
        open={isModalOpen}
        onClose={handleClose}
        className='content-export-modal'
      >
        <UiDialogTitle
          className='header content-export-title'
          error={currentStep.error}
        >
          <div className='title-content'>
            {currentStep.showBack && (
              <UiButton
                variant='text'
                className='title-back-link'
                onClick={currentStep.onBack}
                data-testid='content-export-back-btn'
              >
                <UiIcon icon='ri-arrow-right-line' color='#0C395A' size={16} />
              </UiButton>
            )}
            <div className='title-text'>{currentStep.title}</div>
          </div>
          <div onClick={handleClose} className='flex'>
            <UiIcon icon='ri-close-line' color='#454545' />
          </div>
        </UiDialogTitle>

        <UiDialogContent className='content-export-body'>
          {currentStep.view}
        </UiDialogContent>

        {currentStep.onSubmit && (
          <UiDialogActions
            className={`content-export-actions ${currentStep.actionsClassName}`}
          >
            <div className='m-0'>{currentStep.moreActions}</div>
            <div className='m-0'>
              <UiButton
                variant='contained'
                onClick={currentStep.onSubmit}
                loading={currentStep.submitLoading}
                disabled={currentStep.submitDisabled}
                data-testid={currentStep.submitTestId}
              >
                {currentStep.submitLabel}
              </UiButton>
            </div>
          </UiDialogActions>
        )}
      </StyledContentExportDialog>
    </StyledContentExport>
  );
};

ContentExport.propTypes = {
  platform: PropTypes.string,
  excelOnly: PropTypes.bool,
  randomSort: PropTypes.bool,
  renderTrigger: PropTypes.func,
};

export default ContentExport;
